import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import classNames from "../../utility/classNames";

const PRIMARY_HREFS = ["/dashboard", "/calendar", "/clients"];

// The center "+" FAB creates the current page's natural item. Each add-capable
// page reads `?new=<param>` (see useCreateIntent) and opens its own create
// Modal. Pages with no create action fall back to a new appointment on the
// Dashboard. Longest matching prefix wins so /clients/:id resolves before
// /clients. `target` is the route the FAB navigates to (defaults to the
// current path; only the fallback overrides it to /dashboard).
const CREATE_ACTIONS = [
  { match: "/dashboard", param: "appointment", label: "New appointment" },
  { match: "/calendar", param: "appointment", label: "New appointment" },
  { match: "/clients/", param: "appointment", label: "New appointment" },
  { match: "/clients", param: "client", label: "New client" },
  { match: "/services", param: "service", label: "New service" },
  { match: "/availability", param: "availability", label: "New availability" },
];

const FALLBACK_ACTION = {
  param: "appointment",
  label: "New appointment",
  target: "/dashboard",
};

const resolveCreateAction = (pathname) => {
  const hit = CREATE_ACTIONS.find(
    (a) => pathname === a.match || pathname.startsWith(a.match)
  );
  if (!hit) return FALLBACK_ACTION;
  return { ...hit, target: pathname };
};

const BottomNav = ({ navigation }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const createAction = resolveCreateAction(location.pathname);

  const [dashboardTab, calendarTab, clientsTab] = PRIMARY_HREFS.map((href) =>
    navigation.find((n) => n.href === href)
  );

  const moreTabs = navigation.filter((n) => !PRIMARY_HREFS.includes(n.href));
  const isMoreActive = moreTabs.some((n) => n.current);

  const openCrispChat = () => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:show"]);
      window.$crisp.push(["do", "chat:open"]);
    }
  };

  const handleCreate = () => {
    // Preserve existing query params (e.g. /clients?page=3) when creating on the
    // current page; the fallback targets a different route, so it starts clean.
    const params =
      createAction.target === location.pathname
        ? new URLSearchParams(location.search)
        : new URLSearchParams();
    params.set("new", createAction.param);
    navigate(`${createAction.target}?${params.toString()}`);
  };

  const renderTab = (item) => {
    if (!item) return null;
    return (
      <Link
        key={item.name}
        to={item.href}
        aria-current={item.current ? "page" : undefined}
        className="relative flex-1 flex flex-col items-center justify-center gap-y-1 min-h-[56px] px-1"
      >
        {item.current && (
          <span className="absolute top-0 inset-x-0 h-[2px] bg-accent-500 rounded-b-pill" />
        )}
        <item.icon
          aria-hidden="true"
          className={classNames(
            "h-6 w-6 shrink-0 transition-colors",
            item.current ? "text-accent-500" : "text-ink-400"
          )}
        />
        <span
          className={classNames(
            "text-[11px] font-medium leading-none transition-colors",
            item.current ? "text-ink-900" : "text-ink-500"
          )}
        >
          {item.name}
        </span>
      </Link>
    );
  };

  return (
    <>
      <nav
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-cream-50/95 backdrop-blur border-t border-ink-100 flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {renderTab(dashboardTab)}
        {renderTab(calendarTab)}

        <div className="relative flex-1 flex items-center justify-center min-h-[56px]">
          <button
            type="button"
            onClick={handleCreate}
            aria-label={createAction.label}
            className="-mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-cream-50 shadow-lift ring-4 ring-cream-50 hover:bg-accent-600 focus:outline-none focus-visible:ring-accent-300 active:scale-95 transition-transform"
          >
            <PlusIcon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>

        {renderTab(clientsTab)}

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-current={isMoreActive ? "page" : undefined}
          className="relative flex-1 flex flex-col items-center justify-center gap-y-1 min-h-[56px] px-1"
        >
          {isMoreActive && (
            <span className="absolute top-0 inset-x-0 h-[2px] bg-accent-500 rounded-b-pill" />
          )}
          <EllipsisHorizontalIcon
            aria-hidden="true"
            className={classNames(
              "h-6 w-6 shrink-0",
              isMoreActive ? "text-accent-500" : "text-ink-400"
            )}
          />
          <span
            className={classNames(
              "text-[11px] font-medium leading-none",
              isMoreActive ? "text-ink-900" : "text-ink-500"
            )}
          >
            More
          </span>
        </button>
      </nav>

      <Dialog
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm transition-opacity duration-200 ease-editorial data-[closed]:opacity-0"
        />
        <div
          className="fixed inset-x-0 bottom-0"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <DialogPanel
            transition
            className="bg-cream-50 rounded-t-card shadow-lift transition-transform duration-300 ease-editorial data-[closed]:translate-y-full"
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-ink-200" />
            </div>

            <div className="px-4 pb-2">
              <DialogTitle className="smallcaps text-ink-400">
                More
              </DialogTitle>
            </div>

            <nav className="px-2 pb-3">
              {moreTabs.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMoreOpen(false)}
                  className={classNames(
                    item.current
                      ? "bg-accent-100 text-ink-900"
                      : "text-ink-700 hover:bg-cream-100",
                    "flex items-center gap-x-3 px-3 py-3 rounded-card text-sm font-medium transition-colors"
                  )}
                >
                  <item.icon
                    aria-hidden="true"
                    className={classNames(
                      "h-5 w-5 shrink-0",
                      item.current ? "text-accent-500" : "text-ink-400"
                    )}
                  />
                  {item.name}
                </Link>
              ))}

              <div className="my-1 border-t border-ink-100" />

              <button
                type="button"
                onClick={() => {
                  openCrispChat();
                  setMoreOpen(false);
                }}
                className="flex w-full items-center gap-x-3 px-3 py-3 rounded-card text-sm font-medium text-ink-700 hover:bg-cream-100 transition-colors"
              >
                <QuestionMarkCircleIcon
                  aria-hidden="true"
                  className="h-5 w-5 shrink-0 text-ink-400"
                />
                Help & support
              </button>
            </nav>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default BottomNav;
